import { useEffect, useRef } from 'react';
import { useTheme } from '@sakhtar/design-system/react';
import { echarts, type ECharts, type EChartsCoreOption } from './echarts-setup.js';
import { resolveChartTheme, type ChartTheme } from './chartTheme.js';
import { sameInputs } from './chartInputs.js';

/** Every chart text (axes, legend, tooltip, labels) uses the design-system font, not ECharts' default. */
function withFont(option: EChartsCoreOption, theme: ChartTheme): EChartsCoreOption {
  return {
    ...option,
    textStyle: { fontFamily: theme.fontFamily, ...(option['textStyle'] as object | undefined) },
  };
}

/**
 * Owns one ECharts instance per attached chart host: init once, resize via
 * ResizeObserver, dispose on unmount, and re-render `buildOption` whenever
 * the caller's `deps` change or the light/dark theme flips (re-reading
 * `--sk-*` off the container so a toggle repaints with real colors, not a
 * stale snapshot). Loading, empty and error frames may detach the host;
 * dispose that binding and initialize again when a ready host appears.
 * Callers never touch the echarts instance directly.
 *
 * A full repaint (`notMerge`) recreates every series and replays the entry
 * animation (the line draws in from the left, bars grow from zero). It must
 * therefore only happen when the chart's inputs really changed: a parent that
 * re-renders for an unrelated reason (hover, focus, a timer) while handing
 * over equal data used to restart that animation on every render, so the
 * chart looked hidden for as long as the pointer kept moving over the page.
 */
export function useEChart(
  buildOption: (theme: ChartTheme) => EChartsCoreOption,
  deps: readonly unknown[],
) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const bindingRef = useRef<{
    readonly node: HTMLDivElement;
    readonly chart: ECharts;
    readonly dispose: () => void;
  } | null>(null);
  const buildOptionRef = useRef(buildOption);
  buildOptionRef.current = buildOption;
  const { mode } = useTheme();
  const painted = useRef<{ mode: string; deps: readonly unknown[] } | null>(null);

  // Unmount cleanup is separate from reconciliation: cleaning up on every
  // render would recreate the canvas and restart its entry animation.
  useEffect(
    () => () => {
      bindingRef.current?.dispose();
      bindingRef.current = null;
      painted.current = null;
    },
    [],
  );

  // ChartFrame conditionally renders the ref-bearing host. Reconcile after
  // every commit so an initially loading/empty/failed frame can become ready.
  // An object ref changing by itself does not trigger an effect with [] deps.
  useEffect(() => {
    const el = containerRef.current;
    let binding = bindingRef.current;
    if (binding && binding.node !== el) {
      binding.dispose();
      bindingRef.current = null;
      binding = null;
      painted.current = null;
    }
    if (!el) return;

    if (!binding) {
      const chart = echarts.init(el);
      let disposed = false;
      const paint = () => {
        if (disposed) return;
        const theme = resolveChartTheme(el);
        chart.setOption(withFont(buildOptionRef.current(theme), theme), true);
      };
      const resizeObserver = new ResizeObserver(() => {
        if (!disposed) chart.resize();
      });
      resizeObserver.observe(el);
      binding = {
        node: el,
        chart,
        dispose: () => {
          disposed = true;
          resizeObserver.disconnect();
          chart.dispose();
        },
      };
      bindingRef.current = binding;
      paint();
      painted.current = { mode, deps };
      // Canvas text is drawn once: repaint when the web font finishes loading.
      // A delayed font promise must never repaint a detached/disposed host.
      void document.fonts?.ready.then(paint);
      return;
    }

    const last = painted.current;
    if (last && last.mode === mode && sameInputs(last.deps, deps)) return;
    const theme = resolveChartTheme(el);
    binding.chart.setOption(withFont(buildOptionRef.current(theme), theme), true);
    painted.current = { mode, deps };
    // `deps` is caller-controlled and stable in length per call site.
  });

  return containerRef;
}
